import React from "react";
import { observer } from "mobx-react";
import { Grid, Box, Snackbar, Alert } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import FormExtras from "../../../../common/components/formExtras";

import {
  editionDocumentationWorkflow,
  EDITION_DOCUMENTATION_WORKFLOW_ERROR_STATES,
  EDITION_DOCUMENTATION_WORKFLOW_FORWARD_TRANSITIONS,
} from "../../../../workflows/workflowDefinitions";
import { EditionUpdateStore } from "../../store";
import { runInAction } from "mobx";
import { WorkflowRoadmap } from "../../../../common/controls";
import { InputString } from "../../../common/controls";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import { EditionPublicationLinkCustomField } from "../custom/EditionPublicationLinkCustomField";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { EditionTypeCreate } from "../../../editionType/components/create/EditionTypeCreate";
import { EditionTypeCreateStore } from "../../../editionType/store";
import { EditionContentType } from "../../../../api/graphql";
import { InputNumber } from "../../../common/controls";
import { InputReferenceSet } from "../../../common/controls";
import { MusicalWorkCreate } from "../../../musicalWork/components/create/MusicalWorkCreate";
import { MusicalWorkCreateStore } from "../../../musicalWork/store";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { InputHtmlString } from "../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridColumn } from "../../../common/types";
import { ItemsCRUDCompact } from "../../../common/controls";
import { GridChangeSet } from "../../../common/types";
import { UserAttachmentEditionUpdate } from "../../../userAttachmentEdition/components/update/UserAttachmentEditionUpdate";
import { UserAttachmentEditionUpdateStore } from "../../../userAttachmentEdition/store";
import { FileEditorDialog } from "../../../../common/controls";
import {
  SharesPieChart,
  computeSharesFromWorks,
} from "../../../../common/components/SharesPieChart";
import { EditionSignatureStatusCard } from "../EditionSignatureStatusCard";
import { EditionSendForSignatureDialog } from "../EditionSendForSignatureDialog";
import { PDFViewerDialog } from "../../../../common/components";
import { DownloadIcon, VisibilityIcon } from "../../../../icons";
import { ShowFilesCompact } from "../../../common/controls";
import { ShowDateTime } from "../../../common/controls";


export type EditionUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  documentationStateTransitions: { id: string; label: string }[];
  onDocumentationStateTransition: (transitionName: string) => {};
  documentationStateValue: string;
  documentationStateIsProcessing?: boolean;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  editionTypeSelectedValue?: { displayId: string; id: string } | null;
  onEditionTypeChange?: (value: string) => void;
  onEditionTypeSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  editionTypeOptions?: { displayId: string; id: string }[];
  editionTypeError?: string;
  contentTypeOptions: { id: string; displayId: string }[];
  contentTypeError?: string;
  contentType: EditionContentType;
  publicationLink: string;
  publicationLinkError?: string;
  onPublicationLinkChange: (value: string) => void;
  recordId?: string;
  onContentTypeChange?: (value: EditionContentType) => void;
  price: number;
  priceSelectedValue?: { displayId: string; id: string } | null;
  onPriceChange?: (value: number) => void;
  priceError?: string;
  worksSelectedValues: { displayId: string; id: string }[];
  onWorksChange?: (value: string) => void;
  onWorksSelected?: (value: { displayId: string; id: string }[]) => void;
  worksOptions?: { displayId: string; id: string }[];
  worksOptionsMap?: any;
  worksMiniFormMode?: "inline" | "tooltip";
  worksError?: string;
  viewFormWorks?: (id: string) => void;
  notes: string;
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notesError?: string;
  contractObservation: string;
  contractObservationSelectedValue?: { displayId: string; id: string } | null;
  onContractObservationChange?: (value: string) => void;
  contractObservationError?: string;
  documentationRows: CrudRow[];
  documentationVisibleColumns: GridColumn[];
  associatedDocumentsRows: CrudRow[];
  associatedDocumentsVisibleColumns: GridColumn[];
  userAttachmentsOnRestore: (id: string) => void;
  userAttachmentsOnCommitChanges: (changes: GridChangeSet) => void;
  userAttachmentsOnCommitInlineChange: (
    rowId: string,
    changes: Record<string, any>
  ) => void;
  userAttachmentsValidateChanges: (changes: GridChangeSet) => boolean;
  userAttachmentsRows: CrudRow[];
  userAttachmentsError?: string;
  userAttachmentEditionUpdateStore: UserAttachmentEditionUpdateStore;
  userAttachmentsVisibleColumns: GridColumn[];
  userAttachmentsColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
    }
  >;
  createdAt_: Date | null;
  lastUpdatedAt_: Date | null;
  dummyCRUDRestoredObservable: number;
  editionUpdateStore: EditionUpdateStore;
  worksWithAuthors?: any[];
};

export const EditionUpdateForm: React.FC<EditionUpdateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    documentationStateTransitions,
    onDocumentationStateTransition,
    documentationStateValue,
    documentationStateIsProcessing,
    name = "",
    nameError,
    onNameChange = () => {},
    onEditionTypeSelected = () => {},
    editionTypeOptions = [],
    editionTypeSelectedValue = null,
    editionTypeError,
    onEditionTypeChange = () => {},
    contentTypeOptions = [],
    contentTypeError,
    contentType = "" as EditionContentType,
    publicationLink = "",
    publicationLinkError,
    onPublicationLinkChange = () => {},
    recordId = "",
    onContentTypeChange = () => {},
    price = 0,
    priceError,
    onPriceChange = () => {},
    worksOptionsMap,
    worksMiniFormMode,
    onWorksSelected = () => {},
    worksOptions = [],
    worksSelectedValues,
    worksError,
    onWorksChange = () => {},
    viewFormWorks = () => {},
    notes = "",
    notesError,
    onNotesChange = () => {},
    contractObservation = "",
    contractObservationError,
    onContractObservationChange = () => {},
    documentationRows,
    documentationVisibleColumns,
    associatedDocumentsRows,
    associatedDocumentsVisibleColumns,
    userAttachmentsOnRestore,
    userAttachmentsOnCommitChanges,
    userAttachmentsOnCommitInlineChange,
    userAttachmentsValidateChanges,
    userAttachmentsRows,
    userAttachmentsError,
    userAttachmentEditionUpdateStore,
    userAttachmentsVisibleColumns,
    userAttachmentsColumnTypes,
    createdAt_ = null,
    lastUpdatedAt_ = null,
    dummyCRUDRestoredObservable,
    editionUpdateStore,
    worksWithAuthors = [],
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;
    const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; severity: "success" | "error" }>({
      open: false, message: "", severity: "success"
    });
    // Confirmación final de Enviar a Firmar: muestra quién firma qué (incluida
    // la DJCT futura) antes de disparar la transición real.
    const [sendConfirmOpen, setSendConfirmOpen] = React.useState(false);

    const handleTransition = (transitionId: string) => {
      if (transitionId === "sendForSignature") {
        setSendConfirmOpen(true);
        return;
      }
      onDocumentationStateTransition(transitionId);
    };

    const handleResend = async (editionDocumentationId: string, email: string) => {
      try {
        await editionUpdateStore.resendSignatureEmail(editionDocumentationId, email);
        setSnackbar({ open: true, message: `Email reenviado a ${email}`, severity: "success" });
      } catch {
        setSnackbar({ open: true, message: "Error al reenviar el email", severity: "error" });
      }
    };

    if (userAttachmentEditionUpdateStore.status === "uninitialized") {
      userAttachmentEditionUpdateStore.initialize();
    }

    return (
      <Grid>
        <FormRecordProvider
          recordId={recordId}
          values={{
            documentationState: documentationStateValue,
            name,
            editionType: editionTypeSelectedValue,
            contentType,
            publicationLink,
            price,
            notes,
            contractObservation,
            createdAt_,
            lastUpdatedAt_,
          }}
        >
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelUpdatedEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
            <FormExtras
              entity="Edition"
              mode="update"
              recordId={recordId}
              values={{
                documentationState: documentationStateValue,
                name,
                editionType: editionTypeSelectedValue,
                contentType,
                publicationLink,
                price,
                notes,
                contractObservation,
                createdAt_,
                lastUpdatedAt_,
              }}
              options={{ editionType: editionTypeOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "editionType":
                    onEditionTypeSelected?.(valor);
                    break;
                  case "contentType":
                    onContentTypeChange?.(valor);
                    break;
                  case "publicationLink":
                    onPublicationLinkChange?.(valor);
                    break;
                  case "price":
                    onPriceChange?.(valor);
                    break;
                  case "notes":
                    onNotesChange?.(valor);
                    break;
                  case "contractObservation":
                    onContractObservationChange?.(valor);
                    break;
                }
              }}
            />

          <GroupContainer
            id="editionData"
            errorText={editionUpdateStore.getCardErrors("editionData")}
            title={langStore.groupEditionEditionDataLabel}
            collapsible={true}
            expanded={editionUpdateStore.getCardState("editionData")}
            toggleExpand={() =>
              editionUpdateStore.toggleCardState("editionData")
            }
          >
            <FormControl fullWidth>
              <WorkflowRoadmap
                workflow={editionDocumentationWorkflow}
                currentState={documentationStateValue}
                labels={langStore.EditionDocumentationStateStateLabels}
                transitions={documentationStateTransitions}
                onTransition={handleTransition}
                isProcessing={documentationStateIsProcessing}
                errorStates={EDITION_DOCUMENTATION_WORKFLOW_ERROR_STATES}
                forwardTransitions={
                  EDITION_DOCUMENTATION_WORKFLOW_FORWARD_TRANSITIONS
                }
              />
              {Object.keys(editionUpdateStore.ejemplarPdfFiles).length > 0 && (
                <ShowFilesCompact
                  rows={editionUpdateStore.ejemplarPdfFiles}
                  showLabel={true}
                  label={langStore.EditionEjemplarPdfLabel}
                  menuActions={[
                    {
                      columnName: "__action_view_pdf",
                      onClick: (props: any) =>
                        editionUpdateStore.viewEjemplarPdf(props),
                      icon: <VisibilityIcon />,
                      label: langStore.viewCommand,
                      isActionEnabled: (props: any) =>
                        props.name?.toLowerCase().endsWith(".pdf"),
                    },
                    {
                      columnName: "__action_download",
                      onClick: (props: any) =>
                        editionUpdateStore.downloadEjemplarPdf(props),
                      icon: <DownloadIcon fontSize="small" />,
                      label: langStore.downloadCommand,
                    },
                  ]}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                />
              )}
            </FormControl>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "0 0 400px" }}>
                <FormControl fullWidth>
                  <InputString
                    autoFocus
                    value={name}
                    error={Boolean(nameError)}
                    warning={!nameError && Boolean(editionUpdateStore.warnings["name"])}
                    helperText={nameError || editionUpdateStore.warnings["name"]?.message}
                    label={langStore.EditionNameLabel}
                    onChange={(value: string) => onNameChange(value)}
                    description={langStore.EditionNameDescription}
                  />
                  <InputReference
                    id="editionType"
                    label={langStore.EditionEditionTypeLabel}
                    error={Boolean(editionTypeError)}
                    helperText={editionTypeError || editionUpdateStore.warnings["editionType"]?.message}
                    selectedValue={editionTypeSelectedValue}
                    onChange={(value: string) => onEditionTypeChange(value)}
                    onSelect={(value: { displayId: string; id: string } | null) =>
                      onEditionTypeSelected(value)
                    }
                    options={editionTypeOptions}
                description={langStore.EditionEditionTypeDescription}
                canCreate={rootStore.authStore.hasPermissions("editionType_create")}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new EditionTypeCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <EditionTypeCreate
                        editionTypeCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewEditionType"
                    cancelMethod="cancelNewEditionType"
                  />
                )}
              />
              <InputReference
                id="contentType"
                disabled={false}
                error={Boolean(contentTypeError)}
                helperText={contentTypeError || editionUpdateStore.warnings["contentType"]?.message}
                label={langStore.EditionContentTypeLabel}
                options={contentTypeOptions}
                selectedValue={
                  contentType
                    ? {
                        id: contentType,
                        displayId:
                          contentTypeOptions.find(
                            (o: { id: string }) => o.id === contentType
                          )?.displayId || contentType,
                      }
                    : null
                }
                onChange={() => {}}
                onSelect={(value: { id: string; displayId: string } | null) =>
                  onContentTypeChange(
                    (value?.id as EditionContentType) ||
                      ("" as EditionContentType)
                  )
                }
              />
              <EditionPublicationLinkCustomField
                value={publicationLink}
                error={Boolean(publicationLinkError)}
                helperText={publicationLinkError || editionUpdateStore.warnings["publicationLink"]?.message}
                {...{ optional: true }}
                label={langStore.EditionPublicationLinkLabel}
                description={langStore.EditionPublicationLinkDescription}
                onChange={(value: string) => onPublicationLinkChange(value)}
              />
              <InputNumber
                value={price}
                error={Boolean(priceError)}
                warning={!priceError && Boolean(editionUpdateStore.warnings["price"])}
                helperText={priceError || editionUpdateStore.warnings["price"]?.message}
                label={langStore.EditionPriceLabel}
                decimalScale={2}
                prefix={"$"}
                thousandSeparator={","}
                fixedDecimalScale={true}
                description={langStore.EditionPriceDescription}
                onChange={(value: number) => onPriceChange(value)}
              />
              <InputReferenceSet
                id="works"
                label={langStore.EditionWorksLabel}
                error={Boolean(worksError)}
                helperText={worksError || editionUpdateStore.warnings["works"]?.message}
                selectedValues={worksSelectedValues}
                onChange={(value: string) => onWorksChange(value)}
                onSelect={(value: { displayId: string; id: string }[]) =>
                  onWorksSelected(value)
                }
                options={worksOptions}
                optionsMap={worksOptionsMap}
                miniFormMode={worksMiniFormMode}
                description={langStore.EditionWorksDescription}
                onView={viewFormWorks}
                canCreate={rootStore.authStore.hasPermissions("musicalWork_create")}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new MusicalWorkCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <MusicalWorkCreate
                        musicalWorkCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewMusicalWork"
                    cancelMethod="cancelNewMusicalWork"
                  />
                )}
              />
              <InputText
                value={notes}
                error={Boolean(notesError)}
                warning={!notesError && Boolean(editionUpdateStore.warnings["notes"])}
                helperText={notesError || editionUpdateStore.warnings["notes"]?.message}
                label={langStore.EditionNotesLabel}
                onChange={(value: string) => onNotesChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.EditionNotesDescription}
              />
                </FormControl>
              </Box>
              {worksWithAuthors.length > 0 && (
                <Box sx={{ flex: "1 1 400px", minWidth: 400 }}>
                  <SharesPieChart
                    data={computeSharesFromWorks(worksWithAuthors)}
                    title={
                      worksWithAuthors.length > 1
                        ? "Distribución Promedio Autores y Editoriales"
                        : "Distribución Autores y Editoriales"
                    }
                    subtitle={
                      worksWithAuthors.length > 1
                        ? `Promedio de participación neta sobre ${worksWithAuthors.length} obras`
                        : "Participación neta de autores y editoriales"
                    }
                    valueLabel={
                      worksWithAuthors.length > 1
                        ? "Promedio"
                        : "Participación"
                    }
                  />
                </Box>
              )}
            </Box>
          </GroupContainer>

          <GroupContainer
            id="contracts"
            errorText={editionUpdateStore.getCardErrors("contracts")}
            title={langStore.groupEditionContractsLabel}
            collapsible={true}
            expanded={editionUpdateStore.getCardState("contracts")}
            toggleExpand={() => editionUpdateStore.toggleCardState("contracts")}
          >
            <FormControl fullWidth>
              <InputHtmlString
                value={contractObservation}
                error={Boolean(contractObservationError)}
                helperText={contractObservationError || editionUpdateStore.warnings["contractObservation"]?.message}
                label={langStore.EditionContractObservationLabel}
                onChange={(value: string) => onContractObservationChange(value)}
                fragment={true}
                description={langStore.EditionContractObservationDescription}
              />
            </FormControl>
          </GroupContainer>

          <EditionSignatureStatusCard
            signatureStatuses={editionUpdateStore.signatureStatuses}
            documentationStateValue={documentationStateValue}
            expanded={editionUpdateStore.getCardState("signatureStatus")}
            onToggle={() => editionUpdateStore.toggleCardState("signatureStatus")}
            onResend={handleResend}
          />

          <GroupContainer
            id="editionContracts"
            errorText={editionUpdateStore.getCardErrors("editionContracts")}
            title={langStore.groupEditionEditionContractsLabel}
            collapsible={true}
            expanded={editionUpdateStore.getCardState("editionContracts")}
            toggleExpand={() =>
              editionUpdateStore.toggleCardState("editionContracts")
            }
          >
            <FormControl fullWidth>
              <ShowItemsCompact
                label={langStore.EditionDocumentationLabel}
                description={langStore.EditionDocumentationDescription}
                showLabel={false}
                rows={documentationRows}
                columns={documentationVisibleColumns}
                entityName="EditionDocumentation"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal("editionDocumentation", rowId)
                }
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="editionDocumentation"
            errorText={editionUpdateStore.getCardErrors("editionDocumentation")}
            title={langStore.groupEditionEditionDocumentationLabel}
            collapsible={true}
            expanded={editionUpdateStore.getCardState("editionDocumentation")}
            toggleExpand={() =>
              editionUpdateStore.toggleCardState("editionDocumentation")
            }
          >
            <FormControl fullWidth>
              <ShowItemsCompact
                label={langStore.EditionAssociatedDocumentsLabel}
                description={langStore.EditionAssociatedDocumentsDescription}
                showLabel={false}
                rows={associatedDocumentsRows}
                columns={associatedDocumentsVisibleColumns}
                entityName="AssociatedDocumentEdition"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal(
                    "associatedDocumentEdition",
                    rowId
                  )
                }
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="editionUserAttachments"
            errorText={editionUpdateStore.getCardErrors(
              "editionUserAttachments"
            )}
            title={langStore.groupEditionEditionUserAttachmentsLabel}
            collapsible={true}
            expanded={editionUpdateStore.getCardState("editionUserAttachments")}
            toggleExpand={() =>
              editionUpdateStore.toggleCardState("editionUserAttachments")
            }
          >
            <FormControl fullWidth>
              <ItemsCRUDCompact
                label={langStore.EditionUserAttachmentsLabel}
                description={langStore.EditionUserAttachmentsDescription}
                showLabel={false}
                rows={userAttachmentsRows}
                columns={userAttachmentsVisibleColumns}
                columnTypes={userAttachmentsColumnTypes}
                canCreate={true}
                entityName="UserAttachmentEdition"
                enableDownload={true}
                isEditVisible={() =>
                  rootStore.authStore.hasPermissions(
                    "userAttachmentEdition_update"
                  )
                }
                isDeleteVisible={() =>
                  rootStore.authStore.hasPermissions(
                    "userAttachmentEdition_delete"
                  )
                }
                popupComponent={(row: any) => {
                  if (
                    row &&
                    row.id &&
                    !(typeof row.id === "string" && row.id.startsWith("auto-"))
                  ) {
                    runInAction(() =>
                      editionUpdateStore.userAttachmentsLoadRow(row)
                    );
                  }
                  return (
                    <UserAttachmentEditionUpdate
                      userAttachmentEditionUpdateStore={
                        userAttachmentEditionUpdateStore
                      }
                      showToolbox={false}
                      showSaveFooter={false}
                    />
                  );
                }}
                popupMaxWidth="sm"
                onRestore={(id: string) => userAttachmentsOnRestore(id)}
                onCommitChanges={(changes: GridChangeSet) =>
                  userAttachmentsOnCommitChanges(changes)
                }
                onCommitInlineChange={(
                  rowId: string,
                  changes: Record<string, any>
                ) => userAttachmentsOnCommitInlineChange(rowId, changes)}
                validate={(changes: GridChangeSet) =>
                  userAttachmentsValidateChanges(changes)
                }
                dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                error={userAttachmentsError}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="systemDates"
            errorText={editionUpdateStore.getCardErrors("systemDates")}
            title={langStore.groupEditionSystemDatesLabel}
            collapsible={true}
            expanded={editionUpdateStore.getCardState("systemDates")}
            toggleExpand={() =>
              editionUpdateStore.toggleCardState("systemDates")
            }
          >
            <FormControl fullWidth>
              <ShowDateTime
                label={langStore.EditionCreatedAt_Label}
                description={langStore.EditionCreatedAt_Description}
                value={createdAt_}
              />
              <ShowDateTime
                label={langStore.EditionLastUpdatedAt_Label}
                description={langStore.EditionLastUpdatedAt_Description}
                value={lastUpdatedAt_}
              />
            </FormControl>
          </GroupContainer>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}

        <FileEditorDialog store={editionUpdateStore.fileEditorStore} />
        <PDFViewerDialog
          open={editionUpdateStore.pdfViewerOpen}
          onClose={() => editionUpdateStore.closePdfViewer()}
          pdfUrl={editionUpdateStore.pdfViewerUrl}
          title={editionUpdateStore.pdfViewerTitle}
          warning={editionUpdateStore.pdfViewerWarning}
        />
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
        </Snackbar>
        <EditionSendForSignatureDialog
          open={sendConfirmOpen}
          signatureStatuses={editionUpdateStore.signatureStatuses}
          processing={documentationStateIsProcessing}
          onCancel={() => setSendConfirmOpen(false)}
          onConfirm={() => {
            setSendConfirmOpen(false);
            onDocumentationStateTransition("sendForSignature");
          }}
        />
        </FormRecordProvider>
      </Grid>
    );
  }
);
